import type { ReactNode } from "react";
import { AlertCircle, AlertTriangle, Check, Info, X, type LucideIcon } from "lucide-react";
import { Icon } from "./Icon";
import { cx } from "./cx";

export type ToastTone = "success" | "info" | "warning" | "danger";

export interface ToastProps {
  tone?: ToastTone;
  children?: ReactNode;
  onClose?: () => void;
  className?: string;
}

const TONE: Record<ToastTone, { icon: LucideIcon; className: string }> = {
  success: { icon: Check, className: "bg-success-100 border-green-200 text-success-700" },
  info: { icon: Info, className: "bg-info-100 border-blue-200 text-info-700" },
  warning: { icon: AlertTriangle, className: "bg-warning-100 border-gold-300 text-warning-700" },
  danger: { icon: AlertCircle, className: "bg-danger-100 border-[#e8cdc8] text-danger-700" },
};

/** Transient confirmation. One line, states the outcome. */
export function Toast({ tone = "success", children, onClose, className }: ToastProps) {
  const t = TONE[tone];
  return (
    <div role="status" className={cx("flex items-center gap-4 rounded-md border py-3 pr-3 pl-4 shadow-lg type-body-strong", t.className, className)}>
      <Icon icon={t.icon} size={18} />
      <span className="flex-1">{children}</span>
      {onClose && (
        <button type="button" aria-label="Dismiss" onClick={onClose} className="grid size-8 cursor-pointer place-items-center rounded-sm hover:bg-black/5">
          <Icon icon={X} size={16} />
        </button>
      )}
    </div>
  );
}
